import {englishLabel, FormatLabel, LabelDescriptor} from './projectStatusLabels';

/**
 * The win-chance band vocabulary, beside `projectStageLabels` and `projectStatusLabels` for the
 * same reason they are separate from each other: a reader meets several project-level vocabularies
 * and the distinction is worth seeing in the imports.
 *
 * These bands are NOT a partition. `ZERO` and `HUNDRED` are exact points while the five `OVER_n`
 * tokens are cumulative thresholds, so a project at 60% is matched by both `OVER_30` and
 * `OVER_50`. The labels say so out loud - `over 30 %` rather than `30 %` - because a reader offered
 * a bare `30 %` beside a bare `0 %` would reasonably read the set as disjoint buckets and expect
 * the counts to add up to the total. They do not.
 */

/**
 * Wire token -> message. These reuse web-front's existing `filter_type.project.win_chance.*` keys,
 * the same ones `Util.getProjectWinChanceFilters` resolves for the legacy scheduling filters, so a
 * reader in any locale we ship sees the translated form on day one rather than the English default
 * a fresh `ai_report_builder.*` key would have given them. It also means this surface and the
 * legacy filter cannot drift into two spellings of one vocabulary.
 *
 * Each `defaultMessage` is the string `translations/defaultMessages/defaultMessages.json` holds for
 * that id, character for character. It is only ever read when the id is missing from the active
 * bundle, so a different wording here would not change what a reader sees - it would only make the
 * fallback disagree with every locale, including English, and make a test that asserts the fallback
 * look like it was asserting the rendered label.
 *
 * A `Map` rather than an object literal because the key is a raw wire string: an object lookup
 * answers for `constructor`, `toString` and `__proto__` off the prototype chain, and formatting one
 * of those as a descriptor yields `undefined` where the type promises a string.
 */
const WIN_CHANCE_MESSAGE = new Map<string, LabelDescriptor>([
	['ZERO', {id: 'filter_type.project.win_chance.0', defaultMessage: '0 %'}],
	['OVER_10', {id: 'filter_type.project.win_chance.over_10', defaultMessage: 'over 10 %'}],
	['OVER_30', {id: 'filter_type.project.win_chance.over_30', defaultMessage: 'over 30 %'}],
	['OVER_50', {id: 'filter_type.project.win_chance.over_50', defaultMessage: 'over 50 %'}],
	['OVER_70', {id: 'filter_type.project.win_chance.over_70', defaultMessage: 'over 70 %'}],
	['OVER_90', {id: 'filter_type.project.win_chance.over_90', defaultMessage: 'over 90 %'}],
	['HUNDRED', {id: 'filter_type.project.win_chance.100', defaultMessage: '100 %'}],
]);

/**
 * What a reader is shown for a raw `projectStatus.winChance` band token.
 *
 * An unrecognised token is echoed rather than replaced, matching `stageLabelFor`: a band this
 * client has not heard of still names a real filter the engine can apply, and showing the token
 * tells the reader more than hiding it would. Echoing also means a catalog that adds an eighth band
 * degrades to a readable token instead of a blank row.
 *
 * Note this labels the FILTER's band tokens, not the column's value. The column carries a whole
 * percentage (`45`), which needs no vocabulary - only the `%` suffix the enumerated-value module
 * already appends for a percent-unit field.
 *
 * There is deliberately no "render the whole vocabulary in published order" helper beside this one:
 * the picker builds its candidates from `field.values`, which the catalog already publishes in
 * ascending order, so a second hand-maintained copy of the seven tokens could only drift from it.
 */
export function winChanceLabelFor(value: string, formatLabel: FormatLabel = englishLabel): string {
	const descriptor = WIN_CHANCE_MESSAGE.get(value);
	return descriptor ? formatLabel(descriptor) : value;
}
